import * as React from 'react';
import {useDayzed} from 'dayzed';
import {Trans} from '@lingui/macro';
import {fromJS} from 'immutable';
import {useDispatch, useSelector} from 'react-redux';
import parse from 'date-fns/parse';
import addBusinessDays from 'date-fns/addBusinessDays';
import isBefore from 'date-fns/isBefore';
import isSaturday from 'date-fns/isSaturday';
import isSunday from 'date-fns/isSunday';
import cl from 'classnames';

import ClockIcon from '@components/Icons/ClockIcon';
import EditIcon from '@components/Icons/EditIcon';
import FlatButton from '@components/FlatButton';
import {Popover, PopoverTrigger, PopoverContent} from '@components/Popover';

import {setNewCalendarTests} from '@actions/userCampaign';
import {extractTranslatedLabel, formatDate} from '@services/utilities';

import s from './CalendarSidebar.module.css';

export default function CalendarSidebar({
  selectedTest,
  tests,
  position,
  calendarTests,
  openPreviewTest,
}) {
  return (
    <div id="sk-calendar-sidebar" className={s.ActivitiesSection}>
      <div className={s.Title}>
        <Trans id="calendar.activity">Attività</Trans>
      </div>

      <div className={s.SectionHeader}>
        <div className={s.Title}>Agenda</div>
      </div>

      {tests.size > 0 &&
        selectedTest && (
          <TestDetails
            calendarTests={calendarTests}
            position={position}
            tests={tests}
            testId={selectedTest}
            openPreviewTest={openPreviewTest}
          />
        )}
    </div>
  );
}

function TestDetails({testId, tests, position, calendarTests, openPreviewTest}) {
  const lang = useSelector(state => state.getIn(['userCampaign', 'data', 'lang']));

  const selectedTest = tests.find(item => item.get('id') === testId);
  const previewType = selectedTest.getIn(['jsonData', 'theme', 'preview', 'type']);
  const parsedDate = parse(selectedTest.get('startDate'), 'dd/MM/yyyy', new Date());

  const showOpenCTA = position === selectedTest.get('startDate');

  return (
    <div className={cl(s.Test)}>
      <div className={s.Abbr}>
        <div>{selectedTest.get('abbreviation')}</div>
        <div className={s.Clock}>
          {selectedTest.getIn(['jsonData', 'duration']) ? (
            <>
              <div>{selectedTest.getIn(['jsonData', 'duration'])}</div>
              <ClockIcon />
            </>
          ) : null}
        </div>
      </div>

      <div className={s.Texts}>
        <div>{extractTranslatedLabel(selectedTest, lang, 'name')}</div>
        <div>{extractTranslatedLabel(selectedTest, lang, 'description')}</div>
      </div>

      {selectedTest.get('status') !== 'complete' && (
        <div className={s.Buttons}>
          <Popover useArrow={false}>
            <PopoverTrigger title="Riprogramma" className={s.TriggerButton}>
              <div>{formatDate(parsedDate, 'EEEE d', lang)}</div>
              <div className={s.Edit}>
                <EditIcon customColor="#E64A50" />
              </div>
            </PopoverTrigger>

            <PopoverContent align="start">
              <MiniCalendar
                tests={tests}
                test={selectedTest}
                position={position}
                calendarTests={calendarTests}
              />
            </PopoverContent>
          </Popover>

          {showOpenCTA && (
            <FlatButton onClick={openPreviewTest}>
              <div>
                <Trans id="calendar.start_activity">Inizia l'attività</Trans>
              </div>
            </FlatButton>
          )}
        </div>
      )}
    </div>
  );
}

function MiniCalendar({calendarTests, position, test, tests}) {
  const dispatch = useDispatch();
  const {calendars, getDateProps} = useDayzed({
    showOutsideDays: true,
    date: new Date('01 November 2021'),
  });

  return (
    <div id="sk-calendar-mini" className={s.MiniCalendar}>
      {calendars.map(calendar => (
        <div key={`${calendar.month}${calendar.year}`} className={s.Week}>
          {calendars?.[0].weeks.map((week, weekIndex) => (
            <div key={weekIndex} className={cl(s.CalendarGrid)}>
              {week.map((dateObj, dateIndex) => {
                const {date} = dateObj;
                const isSunOrSat = isSunday(date) || isSaturday(date);
                const formattedDate = formatDate(date, 'dd/MM/yyyy');
                const isFilled = Boolean(calendarTests.get(formattedDate));
                const isCurrentPosition = test.get('startDate') === formattedDate;

                const canBeMovedToThisDate = getCanBeMoved({
                  test,
                  tests,
                  calendarTests,
                  calendarDate: date,
                  position,
                });

                return (
                  <FlatButton
                    {...getDateProps({dateObj})}
                    key={`${calendar.month}${calendar.year}${weekIndex}${dateIndex}`}
                    className={cl(
                      s.Cell,
                      isCurrentPosition && s.Active,
                      isSunOrSat || isFilled || !canBeMovedToThisDate ? s.Ghost : ''
                    )}
                    onClick={() =>
                      moveToThisDate({
                        test,
                        tests,
                        calendarTests,
                        calendarDate: date,
                        position,
                        dispatch,
                      })
                    }
                    disabled={isCurrentPosition || isSunOrSat || isFilled}
                  >
                    <div>{date.getDate()}</div>
                  </FlatButton>
                );
              })}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function getCanBeMoved({test, tests, calendarTests, calendarDate, position}) {
  const parsedPosition = parse(position, 'dd/MM/yyyy', new Date());

  if (isBefore(calendarDate, parsedPosition)) {
    return false;
  }

  let testsThatHaveThisAsPrerequisite = 0;
  let testsThatHaveThisAsPrerequisiteDates = [];
  for (let item of tests) {
    const prerequisites = item.getIn(['jsonData', 'prerequisites'], []);

    if (prerequisites.size === 0) {
      break;
    }

    const foundTest = prerequisites.find(testId => testId == test.get('id'));

    if (foundTest) {
      testsThatHaveThisAsPrerequisite += 1;
      testsThatHaveThisAsPrerequisiteDates.push(item.get('startDate'));
    }
  }

  const isBeforePrerequisites = testsThatHaveThisAsPrerequisiteDates.every(date => {
    const parsedDate = parse(date, 'dd/MM/yyyy', new Date());
    return isBefore(calendarDate, parsedDate);
  });

  if (isBeforePrerequisites) {
    return true;
  }

  if (testsThatHaveThisAsPrerequisite === 0) {
    return true;
  }

  let freeDays = 0;
  for (let index = 0; index < testsThatHaveThisAsPrerequisite + 1; ++index) {
    const nextDay = addBusinessDays(calendarDate, index);
    const formattedNextDay = formatDate(nextDay, 'dd/MM/yyyy');

    if (Boolean(calendarTests.get(formattedNextDay))) {
      break;
    }

    freeDays += 1;
  }

  return freeDays === testsThatHaveThisAsPrerequisite + 1;
}

function moveToThisDate({test, tests, calendarTests, calendarDate, position, dispatch}) {
  let testsThatHaveThisAsPrerequisite = [
    {testId: test.get('id'), startDate: test.get('startDate')},
  ];

  for (let item of tests) {
    const prerequisites = item.getIn(['jsonData', 'prerequisites'], []);

    if (prerequisites.size === 0) {
      break;
    }

    const foundTest = prerequisites.find(testId => testId == test.get('id'));

    if (foundTest) {
      testsThatHaveThisAsPrerequisite.push({
        testId: item.get('id'),
        startDate: item.get('startDate'),
      });
    }
  }

  //MOVE TO NEW PLACE
  let newCalendarTests = calendarTests.toJS();
  testsThatHaveThisAsPrerequisite.forEach((testToBeMoved, index) => {
    const newDate = addBusinessDays(calendarDate, index);
    const formattedDate = formatDate(newDate, 'dd/MM/yyyy');
    newCalendarTests[formattedDate] = {testId: testToBeMoved.testId};
  });

  //REMOVE FROM OLD PLACE
  const testsThatHaveThisAsPrerequisiteIds = testsThatHaveThisAsPrerequisite.map(
    test => test.testId
  );
  calendarTests.keySeq().forEach(date => {
    const testId = calendarTests.getIn([date, 'testId']);
    const isToBeRemoved = testsThatHaveThisAsPrerequisiteIds.includes(testId);

    if (isToBeRemoved) {
      delete newCalendarTests[date];
    }
  });

  dispatch(setNewCalendarTests(newCalendarTests));
}
